import { Enum } from "../support/enum.support";
import { TypeSystem } from "../support/type-system.support";
import { BackendPermission } from "./backend-permission.enum";
import { Logger } from "./logger.model";
import { LoggingLevel } from "./logging-level.enum";

    /**
     * @description
     *  Modelo del token de autenticación JWT (JSON Web Token).
     */

export class AuthenticationToken {

    public "gigot/permission": string[];
    public aud: string;
    public exp: number;
    public iss: string;
    public nam: string;
    public permissions: Set<BackendPermission>;
    public roles: string[];
    public uid: string;

    /**
     * @description
     *  Construye un token de autenticación con una semántica vacía. Nótese
     *  que no es lo mismo que construir un token con valores por defecto. Las
     *  reglas generales de construcción son:
     *
     *      - Campos 'array' se definen como [].
     *      - Campos 'boolean' se definen como 'false'.
     *      - Campos 'Date' se definen como 'undefined'.
     *      - Campos 'enum' se definen como 'undefined'.
     *      - Campos 'number' se definen como 'undefined'.
     *      - Campos 'string' se definen como '' (cadena vacía).
     *      - Campos 'object' se definen llamando a su propio método #empty().
     *      - Campos de la superclase se definen al igual que campos 'object'.
     *      - Campos genéricos se definen como 'undefined'.
     *
     * @returns
     *	Un token JWT vacío.
     */
    public static empty(): AuthenticationToken {
        const authenticationToken: AuthenticationToken = new AuthenticationToken();
        authenticationToken["gigot/permission"] = [];
        authenticationToken.aud = "";
        authenticationToken.exp = undefined;
        authenticationToken.iss = "";
        authenticationToken.nam = "";
        authenticationToken.permissions = new Set<BackendPermission>();
        authenticationToken.roles = [];
        authenticationToken.uid = "";
        return authenticationToken;
    }

    /**
    * @description
    *  Método de reificación de un modelo compatible anémico. Esto permite
    *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
    *  valor de sus propiedades se mantiene.
    *
    * @param anemicAuthenticationToken
    *  El modelo anémico a reificar.
    *
    * @returns
    *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
    *  profundidad desde el modelo anémico.
    */
    public static from(anemicAuthenticationToken: AuthenticationToken) {
        const authenticationToken: AuthenticationToken = AuthenticationToken.empty();
        authenticationToken["gigot/permission"] = TypeSystem
            .flattenize(anemicAuthenticationToken["gigot/permission"]);
        authenticationToken.aud = anemicAuthenticationToken.aud;
        authenticationToken.exp = anemicAuthenticationToken.exp;
        authenticationToken.iss = anemicAuthenticationToken.iss;
        authenticationToken.nam = anemicAuthenticationToken.nam;
        authenticationToken.permissions = AuthenticationToken
            .reifiedPermissions(authenticationToken["gigot/permission"]);
        authenticationToken.roles = TypeSystem
            .flattenize(anemicAuthenticationToken.roles);
        authenticationToken.uid = anemicAuthenticationToken.uid;
        return authenticationToken;
    }

    /**
     * @description
     *  Transforma una lista de permisos en un conjunto de permisos
     *  reificados, es decir, en instancias de permisos existentes en el
     *  backend.
     *
     * @param permissions
     *  La lista de permisos a reificar.
     *
     * @returns
     *  El conjunto de permisos reificados.
     *
     * @throws Error
     *  Si alguno de los permisos no se pudo reificar correctamente a un
     *  permiso de backend.
     */
    public static reifiedPermissions(permissions: string[]): Set<BackendPermission> {
        const permissionSet: Set<BackendPermission> = new Set<BackendPermission>();
        permissions
            .map(permission => permission.replace("Permission.", ""))
            .forEach(permission => {
                const reifiedPermission: BackendPermission = Enum.fromString(BackendPermission, permission);
                if (reifiedPermission === undefined) {
					const logger: Logger = new Logger("authentication-token.model", LoggingLevel.INFORMATION);
					logger.warn(`No se pudo reificar el siguiente permiso desde el token: '${permission}'.`);
					logger.warn("Esto ocurre cuando el usuario posee un nuevo permiso que el frontend no conoce.");
                }
                else {
                    permissionSet.add(reifiedPermission);
                }
            });
        return permissionSet;
    }

    /**
     * @description
     *  Devuelve la fecha de expiración de este token.
     *
     * @returns
     *  La fecha de expiración, o la fecha actual si el token no posee una
     *  fecha especificada. Esto permite que el token se declare
     *  automáticamente inválido por encontrarse en el presente.
     */
    public expirationDate(): Date {
        if (this.exp === undefined || this.exp === null) {
            return new Date();
        }
        else {
            const date = new Date(0);
            date.setUTCSeconds(this.exp);
            return date;
        }
    }

    /**
     * @description
     *  Determina si el token expiró o no. El token expira si su fecha se
     *  encuentra en el pasado o en el presente exacto, usando precisión de
     *  milisegundos.
     *
     * @returns
     *  Verdadero si el token está vencido (inválido), o si no posee una fecha
     *  de expiración asociada. Falso de otro modo.
     */
    public isExpired(): boolean {
        if (this.exp === undefined || this.exp === null) {
            return false;
        }
        else {
            return this.expirationDate().getTime() <= new Date().getTime();
        }
    }
}
